/**
 * Shared Date Range Filter Component
 * 
 * Reusable filter UI for Project Map, Revenue Projections, and future dashboards.
 * Provides preset options, install week selector, custom date range picker,
 * and Save View functionality for quick access to favorite filters.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Calendar } from '../components/ui/calendar';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Target, 
  TrendingUp,
  ChevronDown,
  X,
  Filter,
  Save,
  Bookmark,
  Star,
  Trash2,
  MoreHorizontal
} from 'lucide-react';
import { format } from 'date-fns';
import { 
  DATE_FILTER_PRESETS, 
  DATE_FILTER_LABELS, 
  calculateDateRange,
  getUpcomingInstallWeeks 
} from '../services/dateRangeFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * DateRangeFilter Component
 * 
 * @param {Object} props
 * @param {string} props.value - Current filter preset value
 * @param {Object} props.customRange - { from: Date, to: Date } for custom range
 * @param {function} props.onChange - Callback when filter changes: (preset, customRange) => void
 * @param {boolean} props.showInstallWeeks - Whether to show install week options (default: true)
 * @param {boolean} props.showCustomRange - Whether to show custom range option (default: true)
 * @param {boolean} props.showSaveView - Whether to show save view option (default: true)
 * @param {string} props.pageName - Page identifier for saved views (default: 'all')
 * @param {boolean} props.compact - Compact mode for smaller UI (default: false)
 * @param {string} props.className - Additional CSS classes
 */
export const DateRangeFilter = ({
  value = DATE_FILTER_PRESETS.ALL,
  customRange = { from: null, to: null },
  onChange,
  showInstallWeeks = true,
  showCustomRange = true,
  showSaveView = true,
  pageName = 'all',
  compact = false,
  className = '',
}) => {
  const [localCustomRange, setLocalCustomRange] = useState(customRange);
  const [installWeeks] = useState(() => getUpcomingInstallWeeks(6));
  const [savedViews, setSavedViews] = useState([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [newViewIsDefault, setNewViewIsDefault] = useState(false);
  const [savingView, setSavingView] = useState(false);
  
  // Get the current range info for display
  const rangeInfo = calculateDateRange(value, localCustomRange);
  
  // Fetch saved views
  useEffect(() => {
    if (showSaveView) {
      fetchSavedViews();
    }
  }, [showSaveView, pageName]);
  
  const fetchSavedViews = async () => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/filter-views`, {
        headers,
        params: { page: pageName }
      });
      
      setSavedViews(response.data.views || []);
      
      // Auto-apply default view if no filter is currently set
      if (value === DATE_FILTER_PRESETS.ALL) {
        const defaultView = response.data.views?.find(v => v.is_default);
        if (defaultView) {
          applyView(defaultView);
        }
      }
    } catch (error) {
      console.error('Failed to fetch saved views:', error);
    }
  };
  
  const applyView = (view) => {
    const customRange = view.custom_range_from && view.custom_range_to
      ? { from: new Date(view.custom_range_from), to: new Date(view.custom_range_to) }
      : null;
    
    setLocalCustomRange(customRange || { from: null, to: null });
    onChange?.(view.filter_preset, customRange);
  };
  
  const handleSaveView = async () => {
    if (!newViewName.trim()) {
      toast.error('Please enter a name for this view');
      return;
    }
    
    setSavingView(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${API}/filter-views`, {
        name: newViewName,
        filter_preset: value,
        custom_range_from: localCustomRange.from ? localCustomRange.from.toISOString() : null,
        custom_range_to: localCustomRange.to ? localCustomRange.to.toISOString() : null,
        page: pageName,
        is_default: newViewIsDefault,
      }, { headers });
      
      toast.success(`View "${newViewName}" saved!`);
      setSaveDialogOpen(false);
      setNewViewName('');
      setNewViewIsDefault(false);
      fetchSavedViews();
    } catch (error) {
      console.error('Failed to save view:', error);
      toast.error('Failed to save view');
    } finally {
      setSavingView(false);
    }
  };
  
  const handleDeleteView = async (viewId, viewName) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.delete(`${API}/filter-views/${viewId}`, { headers });
      
      toast.success(`View "${viewName}" deleted`);
      fetchSavedViews();
    } catch (error) {
      console.error('Failed to delete view:', error);
      toast.error('Failed to delete view');
    }
  };
  
  const handleSetDefault = async (viewId) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.put(`${API}/filter-views/${viewId}`, {
        is_default: true
      }, { headers });
      
      toast.success('Default view updated');
      fetchSavedViews();
    } catch (error) {
      console.error('Failed to set default:', error);
      toast.error('Failed to set default');
    }
  };
  
  // Handle preset change
  const handlePresetChange = (newPreset) => {
    if (newPreset === DATE_FILTER_PRESETS.CUSTOM_RANGE) {
      // Don't trigger onChange yet, wait for date selection
      onChange?.(newPreset, localCustomRange);
    } else if (newPreset.startsWith('install_week_')) {
      // Handle install week selection
      const weekIndex = parseInt(newPreset.split('_')[2], 10);
      const week = installWeeks[weekIndex];
      if (week) {
        onChange?.(DATE_FILTER_PRESETS.CUSTOM_RANGE, { from: week.start, to: week.end });
        setLocalCustomRange({ from: week.start, to: week.end });
      }
    } else {
      onChange?.(newPreset, null);
    }
  };
  
  // Handle custom date range selection
  const handleCustomRangeChange = (range) => {
    setLocalCustomRange(range || { from: null, to: null });
    if (range?.from && range?.to) {
      onChange?.(DATE_FILTER_PRESETS.CUSTOM_RANGE, range);
    }
  };
  
  // Clear filter
  const handleClear = () => {
    setLocalCustomRange({ from: null, to: null });
    onChange?.(DATE_FILTER_PRESETS.ALL, null);
  };

  if (compact) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Select value={value} onValueChange={handlePresetChange}>
          <SelectTrigger className="w-48 dark:bg-slate-700 dark:border-slate-600">
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-slate-400" />
              <SelectValue placeholder="Filter by date" />
            </div>
          </SelectTrigger>
          <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
            <SelectItem value={DATE_FILTER_PRESETS.ALL}>All Jobs</SelectItem>
            <SelectItem value={DATE_FILTER_PRESETS.NEXT_7_DAYS}>Next 7 Days</SelectItem>
            <SelectItem value={DATE_FILTER_PRESETS.NEXT_14_DAYS}>Next 14 Days</SelectItem>
            <SelectItem value={DATE_FILTER_PRESETS.END_OF_MONTH}>End of Month</SelectItem>
            <SelectItem value={DATE_FILTER_PRESETS.END_OF_QUARTER}>End of Quarter</SelectItem>
            <SelectItem value={DATE_FILTER_PRESETS.END_OF_YEAR}>End of Year</SelectItem>
            {showInstallWeeks && (
              <>
                <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 border-t border-slate-700 mt-1">
                  Install Weeks
                </div>
                {installWeeks.slice(0, 4).map((week, idx) => (
                  <SelectItem key={week.value} value={week.value}>
                    {week.label}
                  </SelectItem>
                ))}
              </>
            )}
            {showCustomRange && (
              <SelectItem value={DATE_FILTER_PRESETS.CUSTOM_RANGE}>Custom Range...</SelectItem>
            )}
          </SelectContent>
        </Select>
        
        {showCustomRange && value === DATE_FILTER_PRESETS.CUSTOM_RANGE && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="dark:bg-slate-700 dark:border-slate-600">
                {localCustomRange.from && localCustomRange.to 
                  ? `${format(localCustomRange.from, 'MMM d')} - ${format(localCustomRange.to, 'MMM d')}`
                  : 'Pick dates'
                }
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 dark:bg-slate-800" align="start">
              <Calendar
                mode="range"
                selected={localCustomRange}
                onSelect={handleCustomRangeChange}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
        )}
        
        {value !== DATE_FILTER_PRESETS.ALL && (
          <Button variant="ghost" size="sm" onClick={handleClear}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <Card className={`dark:bg-slate-800/60 dark:border-slate-700 ${className}`}>
      <CardContent className="pt-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-blue-400" />
            <span className="text-sm font-medium text-slate-200">Schedule Filter:</span>
          </div>
          
          {/* Preset Buttons */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={value === DATE_FILTER_PRESETS.ALL ? 'default' : 'outline'}
              size="sm"
              onClick={() => handlePresetChange(DATE_FILTER_PRESETS.ALL)}
              className={value === DATE_FILTER_PRESETS.ALL 
                ? 'bg-blue-600 hover:bg-blue-700' 
                : 'dark:bg-slate-700 dark:border-slate-600 dark:hover:bg-slate-600'}
            >
              All
            </Button>
            <Button
              variant={value === DATE_FILTER_PRESETS.NEXT_7_DAYS ? 'default' : 'outline'}
              size="sm"
              onClick={() => handlePresetChange(DATE_FILTER_PRESETS.NEXT_7_DAYS)}
              className={value === DATE_FILTER_PRESETS.NEXT_7_DAYS 
                ? 'bg-emerald-600 hover:bg-emerald-700' 
                : 'dark:bg-slate-700 dark:border-slate-600 dark:hover:bg-slate-600'}
            >
              <Clock className="h-3 w-3 mr-1" />
              7 Days
            </Button>
            <Button
              variant={value === DATE_FILTER_PRESETS.NEXT_14_DAYS ? 'default' : 'outline'}
              size="sm"
              onClick={() => handlePresetChange(DATE_FILTER_PRESETS.NEXT_14_DAYS)}
              className={value === DATE_FILTER_PRESETS.NEXT_14_DAYS 
                ? 'bg-blue-600 hover:bg-blue-700' 
                : 'dark:bg-slate-700 dark:border-slate-600 dark:hover:bg-slate-600'}
            >
              <CalendarIcon className="h-3 w-3 mr-1" />
              14 Days
            </Button>
            <Button
              variant={value === DATE_FILTER_PRESETS.END_OF_MONTH ? 'default' : 'outline'}
              size="sm"
              onClick={() => handlePresetChange(DATE_FILTER_PRESETS.END_OF_MONTH)}
              className={value === DATE_FILTER_PRESETS.END_OF_MONTH 
                ? 'bg-purple-600 hover:bg-purple-700' 
                : 'dark:bg-slate-700 dark:border-slate-600 dark:hover:bg-slate-600'}
            >
              <Target className="h-3 w-3 mr-1" />
              EOM
            </Button>
            <Button
              variant={value === DATE_FILTER_PRESETS.END_OF_QUARTER ? 'default' : 'outline'}
              size="sm"
              onClick={() => handlePresetChange(DATE_FILTER_PRESETS.END_OF_QUARTER)}
              className={value === DATE_FILTER_PRESETS.END_OF_QUARTER 
                ? 'bg-orange-600 hover:bg-orange-700' 
                : 'dark:bg-slate-700 dark:border-slate-600 dark:hover:bg-slate-600'}
            >
              <TrendingUp className="h-3 w-3 mr-1" />
              EOQ
            </Button>
          </div>
          
          {/* Install Week Dropdown */}
          {showInstallWeeks && (
            <Select 
              value={value.startsWith('install_week_') ? value : ''} 
              onValueChange={handlePresetChange}
            >
              <SelectTrigger className="w-48 dark:bg-slate-700 dark:border-slate-600">
                <SelectValue placeholder="Install Week..." />
              </SelectTrigger>
              <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                {installWeeks.map((week) => (
                  <SelectItem key={week.value} value={week.value}>
                    {week.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {/* Custom Range Picker */}
          {showCustomRange && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={value === DATE_FILTER_PRESETS.CUSTOM_RANGE ? 'default' : 'outline'}
                  size="sm"
                  className={value === DATE_FILTER_PRESETS.CUSTOM_RANGE 
                    ? 'bg-cyan-600 hover:bg-cyan-700' 
                    : 'dark:bg-slate-700 dark:border-slate-600 dark:hover:bg-slate-600'}
                >
                  <CalendarIcon className="h-3 w-3 mr-1" />
                  {value === DATE_FILTER_PRESETS.CUSTOM_RANGE && localCustomRange.from && localCustomRange.to
                    ? `${format(localCustomRange.from, 'MMM d')} - ${format(localCustomRange.to, 'MMM d')}`
                    : 'Custom Range'
                  }
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 dark:bg-slate-800" align="start">
                <Calendar
                  mode="range"
                  selected={localCustomRange}
                  onSelect={handleCustomRangeChange}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          )}
          
          {/* Saved Views Dropdown */}
          {showSaveView && savedViews.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="dark:bg-slate-700 dark:border-slate-600 dark:hover:bg-slate-600"
                >
                  <Bookmark className="h-3 w-3 mr-1" />
                  Saved Views
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="dark:bg-slate-800 dark:border-slate-600 w-56">
                {savedViews.map((view) => (
                  <DropdownMenuItem 
                    key={view.id}
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => applyView(view)}
                  >
                    <div className="flex items-center gap-2">
                      {view.is_default && <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />}
                      <span>{view.name}</span>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                          <MoreHorizontal className="h-3 w-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="dark:bg-slate-800 dark:border-slate-600">
                        {!view.is_default && (
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleSetDefault(view.id); }}>
                            <Star className="h-3 w-3 mr-2" />
                            Set as Default
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          className="text-red-400"
                          onClick={(e) => { e.stopPropagation(); handleDeleteView(view.id, view.name); }}
                        >
                          <Trash2 className="h-3 w-3 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          {/* Save View Button */}
          {showSaveView && value !== DATE_FILTER_PRESETS.ALL && (
            <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="dark:bg-slate-700 dark:border-slate-600 dark:hover:bg-slate-600"
                >
                  <Save className="h-3 w-3 mr-1" />
                  Save View
                </Button>
              </DialogTrigger>
              <DialogContent className="dark:bg-slate-800 dark:border-slate-700">
                <DialogHeader>
                  <DialogTitle className="dark:text-white">Save Filter View</DialogTitle>
                  <DialogDescription className="dark:text-slate-400">
                    Save this filter configuration for quick access. Current filter: {rangeInfo.label}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="view-name" className="dark:text-slate-200">View Name</Label>
                    <Input
                      id="view-name"
                      placeholder="e.g., This Week's Installs, Q1 Revenue"
                      value={newViewName}
                      onChange={(e) => setNewViewName(e.target.value)}
                      className="dark:bg-slate-700 dark:border-slate-600"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="is-default"
                      checked={newViewIsDefault}
                      onCheckedChange={setNewViewIsDefault}
                    />
                    <Label htmlFor="is-default" className="dark:text-slate-200 cursor-pointer">
                      Set as default view
                    </Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveView} disabled={savingView}>
                    {savingView ? 'Saving...' : 'Save View'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
        
        {/* Active Filter Badge */}
        {value !== DATE_FILTER_PRESETS.ALL && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-700">
            <span className="text-xs text-slate-400">Active:</span>
            <Badge 
              variant="secondary" 
              className="bg-blue-500/20 text-blue-300 border-blue-500/30 flex items-center gap-1"
            >
              <CalendarIcon className="h-3 w-3" />
              {rangeInfo.label}
              <X 
                className="h-3 w-3 ml-1 cursor-pointer hover:text-white" 
                onClick={handleClear}
              />
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DateRangeFilter;
